import { Loader2, X } from 'lucide-react';

import { cn } from '../../utils/cn';
import { formatDateTime } from '../../utils/datetime';
import { addDays } from './date-range';

import { KVALITET_PRESETS, type KvalitetState } from './kvalitet-url-state';

interface KvalitetFilterBarProps {
  state: KvalitetState;
  today: string;
  activePreset: string | null;
  promptVersions: string[];
  promptVersion: string | null;
  loading: boolean;
  onPeriod: (period: { from: string; to: string }) => void;
  onPromptVersion: (promptVersion: string) => void;
  onClearJudged: () => void;
}

export const KvalitetFilterBar = ({
  state,
  today,
  activePreset,
  promptVersions,
  promptVersion,
  loading,
  onPeriod,
  onPromptVersion,
  onClearJudged,
}: KvalitetFilterBarProps) => (
  <div className="space-y-3">
    <div className="flex items-center gap-3 flex-wrap">
      <span className="text-sm font-medium text-slate-700">Tidsperiod:</span>

      <div className="flex items-center gap-1">
        {KVALITET_PRESETS.map((preset) => (
          <button
            key={preset.key}
            type="button"
            onClick={() => onPeriod({ from: addDays(today, -preset.days), to: today })}
            className={cn(
              'rounded-lg border px-3 py-2 text-sm transition',
              activePreset === preset.key
                ? 'border-blue-500 bg-blue-50 text-blue-700 font-medium'
                : 'border-slate-300 text-slate-700 hover:bg-slate-50',
            )}
          >
            {preset.label}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-1.5">
        <input
          type="date"
          aria-label="Från datum"
          value={state.from}
          max={state.to}
          onChange={(event) =>
            event.target.value && onPeriod({ from: event.target.value, to: state.to })
          }
          className="text-sm focus:outline-none"
        />
        <span className="text-slate-400">→</span>
        <input
          type="date"
          aria-label="Till datum"
          value={state.to}
          min={state.from}
          max={today}
          onChange={(event) =>
            event.target.value && onPeriod({ from: state.from, to: event.target.value })
          }
          className="text-sm focus:outline-none"
        />
      </div>

      {promptVersions.length > 0 && promptVersion && (
        <label className="flex items-center gap-2 text-sm text-slate-700">
          Promptversion
          <select
            value={promptVersion}
            onChange={(event) => onPromptVersion(event.target.value)}
            className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm focus:outline-none"
          >
            {promptVersions.map((version) => (
              <option key={version} value={version}>
                {version}
              </option>
            ))}
          </select>
        </label>
      )}

      {loading && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
    </div>

    {state.judged && (
      <span className="inline-flex items-center gap-2 rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-700">
        Domar skrivna {formatDateTime(state.judged.from)}–{formatDateTime(state.judged.to)}
        <button
          type="button"
          onClick={onClearJudged}
          aria-label="Visa perioden efter sessionernas datum"
          title="Visa perioden efter sessionernas datum"
          className="rounded-full p-0.5 hover:bg-indigo-100"
        >
          <X className="h-3 w-3" />
        </button>
      </span>
    )}
  </div>
);
